import KollectiveXHR from '../interceptor/KollectiveXHR';
import addHlsBitrateTracker from './addHlsBitrateTracker';
import {
  VideoJsTech,
  VideoJSPlayerExtended,
  XhrNoAlterFunction,
  XhrOptions,
  VjsEventTargetV7,
  VHSXhr,
  VjsEventTargetV8
} from '../player/interfaces';
import logger from '../log';
import { checkVersionGE } from '../util/checkVersion';
import { VideoJSOptions, VideoJSPlayer } from '@kollective-frontend/ksdk-api';
import { InterceptBinder } from '../player/utils/InterceptBinder';
import { CleanupMethod } from '../player/BaseInteraction';

const log = logger.getLogger(__filename);

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    videojs: any;
  }
}

function validRequestOptions(options: XhrOptions): null | XhrOptions {
  if (options?.uri && options.xhr) {
    return options;
  } else {
    return null;
  }
}

/**
 * Bind intercept to vidoejs instance through hls tech.
 * see: https://github.com/videojs/http-streaming#hlsxhr
 * @param player - videojs player instance
 * @param playerOptions - options for the player instance player instance
 * @param binder - the getOptions and shouldIntercept functions.
 * @returns void
 */
export default function bindVideoJsToWebRTC(
  player: VideoJSPlayer,
  playerOptions: VideoJSOptions,
  binder: InterceptBinder,
  postCleanupMethod: (x: CleanupMethod) => void
): void {
  const pl = player as VideoJSPlayerExtended;
  if (checkVersionGE({ min: '8.4.0', version: window.videojs?.VERSION })) {
    setupVideoJs(pl, playerOptions, binder, postCleanupMethod);
  } else {
    setupOldVersion(pl, playerOptions, binder, postCleanupMethod);
  }
}

const warnMissingGlobalXhr = (name: string) => {
  log.warn(
    `Unable to hook videojs's "${name}", which allows us to get the master playlist.`,
    'Package "videojs" needs to be available on the window.'
  );
};

export const setupOldVersion = (
  player: VideoJSPlayerExtended,
  playerOptions: VideoJSOptions,
  binder: InterceptBinder,
  postCleanupMethod: (x: CleanupMethod) => void
): void => {
  const optionsBefore = playerOptions.beforeRequest as XhrNoAlterFunction;
  player.ready(function () {
    let previousBefore: XhrNoAlterFunction;
    const kBefore = function (options: XhrOptions): XhrOptions {
      const opts = binder.getOptions() || {};
      log.trace('intercepting request', options);
      if (previousBefore) {
        const out = previousBefore(options);
        // in case the previous before call doesn't return the modified options.
        if (validRequestOptions(out)) {
          options = out;
        }
      }
      if (optionsBefore) {
        const out = optionsBefore(options);
        if (validRequestOptions(out)) {
          options = out;
        }
      }
      if (binder.shouldIntercept()) {
        options.xhr = new KollectiveXHR(opts);
      }

      return options;
    };

    // See https://github.com/videojs/video.js/issues/2617 for the reasoning around this parameter.
    const tech = player.tech({ IWillNotUseThisInPlugins: true });

    postCleanupMethod(addHlsBitrateTracker(player, tech));

    // SRV-1440
    // We need to set the global before/onRequest handler so the master playlist gets captured.
    // This will only work if the videojs is available on the window.
    let previousGlobalBefore: XhrNoAlterFunction | undefined;
    const vjs = window.videojs;

    const globalXhr: VjsEventTargetV7 = (vjs?.Vhs || vjs?.Hls)?.xhr;
    if (globalXhr) {
      previousGlobalBefore = globalXhr.beforeRequest;
      log.debug('videojs.Vhs.xhr.beforeRequest has been set');
      globalXhr.beforeRequest = kBefore;
    } else {
      warnMissingGlobalXhr('beforeRequest');
    }

    const interval = setInterval(() => {
      // for whatever reason, the tech isn't always available when the player is ready.
      // This interval validates the tech's state.
      const playerXhr = getPlayerXhrFromTech(tech) as VjsEventTargetV7;
      if (playerXhr) {
        // Done with this interval.
        clearInterval(interval);
        // be like videojs - https://github.com/videojs/http-streaming/blob/e59428a0f16e6ff44f5a327a9fcc137a3c13d915/src/xhr.js#L30
        if (typeof playerXhr.beforeRequest === 'function') {
          previousBefore = playerXhr.beforeRequest;
        }
        playerXhr.beforeRequest = kBefore;
        if (globalXhr) {
          globalXhr.beforeRequest = previousGlobalBefore;
        }
        const vhs = tech.vhs ?? tech.hls;
        // In case the user resets the source stream, this will listen for the handler to
        // be disposed and then restart the binding process.
        if (vhs) {
          vhs.on('dispose', () => {
            if (!binder.resetIsControlled()) {
              bindVideoJsToWebRTC(
                player,
                playerOptions,
                binder,
                postCleanupMethod
              );
            }
          });
        }
      }
    }, 2);
    postCleanupMethod(() => {
      if (globalXhr) {
        globalXhr.beforeRequest = previousGlobalBefore;
      }
      const playerXhr = getPlayerXhrFromTech(tech) as VjsEventTargetV7;
      if (playerXhr) {
        playerXhr.beforeRequest = previousBefore;
      }
    });
  });
};

const getPlayerXhrFromTech = (tech: VideoJsTech): VHSXhr | undefined => {
  return (tech?.vhs ?? tech?.hls)?.xhr;
};

export const setupVideoJs = (
  player: VideoJSPlayerExtended,
  playerOptions: VideoJSOptions,
  binder: InterceptBinder,
  postCleanupMethod: (x: CleanupMethod) => void
): void => {
  player.ready(function () {
    const kBefore = function (options: XhrOptions) {
      const opts = binder.getOptions() || {};
      log.trace('intercepting request', options);
      if (binder.shouldIntercept()) {
        options.xhr = new KollectiveXHR(opts);
      }
      return options;
    };

    // See https://github.com/videojs/video.js/issues/2617 for the reasoning around this parameter.
    const tech = player.tech({ internalPlugin: true });

    postCleanupMethod(addHlsBitrateTracker(player, tech));

    // SRV-1440
    // We need to set the global before/onRequest handler so the master playlist gets captured.
    // This will only work if the videojs is available on the window.
    const globalXhr = window.videojs?.Vhs?.xhr;
    if (typeof globalXhr?.onRequest === 'function') {
      log.debug('videojs.Vhs.xhr.onRequest has been set');
      globalXhr.onRequest(kBefore);
    } else {
      warnMissingGlobalXhr('onRequest');
    }

    const interval = setInterval(() => {
      // for whatever reason, the tech isn't always available when the player is ready.
      // This interval validates the tech's state.
      const playerXhr: VjsEventTargetV8 | undefined = tech.vhs?.xhr;
      if (playerXhr?.onRequest && tech.vhs) {
        clearInterval(interval);
        log.debug('player.tech().vhs.xhr.onRequest has been set');
        playerXhr.onRequest(kBefore);
        // In case the user resets the source stream, this will listen for the handler to
        // be disposed and then restart the binding process.
        tech.vhs.on('dispose', () => {
          if (!binder.resetIsControlled()) {
            bindVideoJsToWebRTC(
              player,
              playerOptions,
              binder,
              postCleanupMethod
            );
          }
        });
      }
    }, 2);
    postCleanupMethod(() => {
      if (typeof globalXhr?.offRequest === 'function') {
        globalXhr.offRequest(kBefore);
      }
      const playerXhr: VjsEventTargetV8 | undefined = tech.vhs?.xhr;
      if (typeof playerXhr?.offRequest === 'function' && tech.vhs) {
        playerXhr.offRequest(kBefore);
      }
    });
  });
};
